import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PRODUCTS, CUSTOM_BASE_PRODUCTS, DESIGN_FEE_VECTORIAL, DESIGN_FEE_FOTO_DIRECTA } from '../constants';
import { API_BASE } from '../services/api';

interface SuccessProps {
  clearCart: () => void;
}

interface OrderItem {
  productId: string | null;
  name: string;
  quantity: number;
  price: number;
  size: string;
  color: string;
}

interface OrderDetails {
  items: OrderItem[];
  shipping: number;
  total: number;
  customer: {
    email: string;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    zip: string;
    country: string;
  };
}

// Para poder enseñar la foto del producto a partir del id que devuelve el backend
const ALL_PRODUCTS = [...PRODUCTS, ...CUSTOM_BASE_PRODUCTS, DESIGN_FEE_VECTORIAL, DESIGN_FEE_FOTO_DIRECTA];
const imageForProduct = (productId: string | null): string =>
  ALL_PRODUCTS.find(p => p.id === productId)?.image || '/logo_negro.png';

const COUNTRY_NAMES: Record<string, string> = {
  ES: 'España', PT: 'Portugal', FR: 'Francia', IT: 'Italia', DE: 'Alemania',
  AD: 'Andorra', GB: 'Reino Unido', BE: 'Bélgica', NL: 'Países Bajos', CH: 'Suiza',
};

// El resumen se pide al backend con el session_id que Stripe añade a la URL,
// así siempre refleja lo que de verdad se ha cobrado (nada de localStorage).
// Los emails de confirmación los envía el webhook (backend/api/stripe-webhook.mjs).
const Success: React.FC<SuccessProps> = ({ clearCart }) => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');

  useEffect(() => {
    clearCart();
    // Limpieza de datos antiguos que ya no se usan
    localStorage.removeItem('sanan_last_order');
    localStorage.removeItem('last_notify_time');
  }, [clearCart]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    fetch(`${API_BASE}/api/get-order?session_id=${encodeURIComponent(sessionId)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Error HTTP: ${res.status}`);
        return res.json();
      })
      .then((data: OrderDetails) => {
        setOrder(data);
        setStatus('ok');
      })
      .catch((err) => {
        console.error('Error recuperando el pedido:', err);
        setStatus('error');
      });
  }, [sessionId]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 py-20 bg-gray-50">
      <div className="w-full max-w-[650px] bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden animate-fade-in">

        <div className="flex flex-col items-center pt-16 pb-10 px-6 text-center">
          <div className="size-20 rounded-full bg-green-100 flex items-center justify-center mb-8 pulse-ring">
            <span className="material-symbols-outlined text-5xl text-green-600 font-bold">check_circle</span>
          </div>
          <h1 className="text-4xl font-black tracking-tighter uppercase mb-4 italic">¡Pedido Confirmado!</h1>
          <p className="text-gray-500 text-lg max-w-sm font-medium italic">
            Tu pedido ya está en preparación!
          </p>
          <p className="mt-4 text-green-600 text-sm font-medium">
            En unos minutos recibirás un email con los detalles de tu pedido.
          </p>
        </div>

        {status === 'loading' && (
          <div className="px-10 mb-12 flex items-center justify-center gap-3 text-gray-400">
            <span className="size-5 border-2 border-gray-200 border-t-primary rounded-full animate-spin"></span>
            <span className="text-xs font-bold uppercase tracking-widest">Cargando tu pedido...</span>
          </div>
        )}

        {status === 'error' && (
          <div className="px-10 mb-12 text-center">
            <p className="text-sm text-gray-500 font-medium">
              No hemos podido cargar el resumen aquí, pero tu pago se ha procesado correctamente.
              Encontrarás todos los detalles en el email de confirmación.
            </p>
          </div>
        )}

        {status === 'ok' && order && (
          <div className="px-10 mb-8">
            <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400 mb-4 italic">Resumen</h3>
            <div className="space-y-4 border-y border-gray-50 py-4 mb-4">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <img src={imageForProduct(item.productId)} className="size-12 rounded-lg object-cover" alt={item.name} />
                  <div className="flex-1">
                    <h4 className="text-xs font-black uppercase italic">{item.name}</h4>
                    <p className="text-[9px] text-gray-400 font-bold uppercase">
                      {item.size} • {item.color} x{item.quantity}
                    </p>
                  </div>
                  <span className="text-sm font-black italic">
                    {(item.price * item.quantity).toFixed(2)}€
                  </span>
                </div>
              ))}
              {order.shipping > 0 && (
                <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-gray-400">
                  <span>Gastos de envío</span>
                  <span className="text-black italic">{order.shipping.toFixed(2)}€</span>
                </div>
              )}
            </div>

            <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400 mb-4 italic">
              Destino del Envío
            </h3>
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <p className="text-xs font-black uppercase italic mb-1">
                {order.customer.firstName} {order.customer.lastName}
              </p>
              <p className="text-xs text-gray-600 font-medium">{order.customer.address}</p>
              <p className="text-xs text-gray-600 font-medium">
                {order.customer.zip}, {order.customer.city}, {COUNTRY_NAMES[order.customer.country] || order.customer.country}
              </p>
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Total Pagado
                </span>
                <span className="text-xl font-black italic">{order.total.toFixed(2)}€</span>
              </div>
            </div>
          </div>
        )}

        <div className="px-10 pb-16 flex flex-col gap-4">
          <Link
            to="/"
            className="w-full bg-black text-white font-bold h-16 rounded-xl flex items-center justify-center gap-2 uppercase tracking-widest italic shadow-xl"
          >
            Volver al Inicio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Success;
